import { Controller } from './controller.js';

/**
 * Component - A resolved unit at one level of the chain.
 * Isomorphic base class — server and client extend with their own resolve().
 */
export class Component {
	crate = null;              // Crate name (e.g., 'admin', 'frontend1')
	component = null;          // Component name within crate (e.g., 'layout', 'profile')
	view = null;               // Path to view file (adapter-dependent: .vdrl, .js, etc.)
	frontendHandler = null;    // Relative path for client import
	provider = null;           // Path to provider.js
	controller = null;         // Path to hybrid controller class

	/**
	 * Import and instantiate the hybrid controller class.
	 * If no controller file, returns a base Controller instance.
	 * If controller file exists, resolve() already validated it has a valid class.
	 */
	async instantiateController(data = {}) {
		if (this.controller) {
			const module = await import(this.controller);
			const Ctrl = module.default;
			if (Ctrl && (Ctrl.$isController || (Ctrl.prototype && Ctrl.prototype.constructor !== Object))) {
				return new Ctrl(data);
			}
		}
		return new Controller(data);
	}

	/**
	 * Execute provider's default export.
	 * If provider file exists, resolve() already validated it has exports.
	 * If default export exists, it must return a truthy value (object).
	 */
	async executeProvider(patterns = {}, route = {}) {
		if (!this.provider) return {};

		const module = await import(this.provider);
		if (module.default) {
			const data = await module.default(patterns, route);
			if (!data) {
				throw new Error(`Provider '${this.crate}/${this.component}' default export returned ${data === null ? 'null' : typeof data}`);
			}
			return data;
		}
		return {};
	}

	/**
	 * Load view template function.
	 * If view file exists, resolve() already validated it has a default export.
	 */
	async loadView() {
		if (!this.view) return null;
		const { default: view } = await import(this.view);
		return view;
	}
}

export default Component;
